/**
 * IndexedDB 持久层 —— 只实现 {@link TilePersistence} 的 6 个原语。
 *
 * ## 两张表
 *
 * `tiles`（缓存键 → 字节）与 `meta`（缓存键 → 大小/时间戳/状态）。分开的理由见
 * `tileStore.ts`：覆盖判定与 LRU 淘汰只需要 `meta`，不必把几百 MB 的 `ArrayBuffer`
 * 读进内存。两张表在**同一个事务**里写，所以不会出现"元数据说有、字节没了"
 * （那种不一致会让覆盖判定撒谎）。
 *
 * 读一条记录时两张表一起读（同一个只读事务），把元数据和字节合成完整的
 * {@link TileEntry}：TTL 与 LRU 都依赖元数据里的时间戳，只读字节表会丢掉它们，
 * 于是"过期"会退化成"永不过期"。
 *
 * ## 打不开库怎么办
 *
 * 隐私模式、被策略禁用的存储、配额耗尽都会让 `indexedDB.open` 失败或抛错。
 * 这时**不能**让地图起不来 —— 退化成内存缓存，并把这件事如实告诉用户
 * （见 `tileService.ts`）：刷新后缓存会丢，但这一次会话仍然完整。
 */

import type { TileEntry, TileEntryMeta, TilePersistence } from './tileStore';

const DB_NAME = 'hgc-map-tiles';
const DB_VERSION = 1;
const TILES_STORE = 'tiles';
const META_STORE = 'meta';
const SOURCE_INDEX = 'bySource';

export interface OpenTileDatabaseResult {
  readonly persistence: TilePersistence | null;
  /** 打不开的原因（中文），`null` 表示成功 */
  readonly reason: string | null;
}

/** 打开（必要时创建）数据库。任何失败都只是"退回内存缓存"，不抛给调用方。 */
export async function openTileDatabase(
  factory: IDBFactory | undefined = globalThis.indexedDB,
): Promise<OpenTileDatabaseResult> {
  if (factory === undefined) {
    return { persistence: null, reason: '这个浏览器环境没有 IndexedDB' };
  }
  try {
    return { persistence: createIdbPersistence(await openDatabase(factory)), reason: null };
  } catch (error) {
    return { persistence: null, reason: describeError(error) };
  }
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const open = factory.open(DB_NAME, DB_VERSION);
    open.onupgradeneeded = () => {
      const database = open.result;
      if (!database.objectStoreNames.contains(TILES_STORE)) {
        database.createObjectStore(TILES_STORE, { keyPath: 'key' });
      }
      if (!database.objectStoreNames.contains(META_STORE)) {
        const meta = database.createObjectStore(META_STORE, { keyPath: 'key' });
        meta.createIndex(SOURCE_INDEX, 'sourceId', { unique: false });
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error('打开 IndexedDB 失败'));
    open.onblocked = () => reject(new Error('另一个标签页正占用地图缓存数据库，暂时无法升级'));
  });
}

function describeError(error: unknown): string {
  if (error instanceof Error && error.message !== '') return error.message;
  return '浏览器拒绝提供本地存储';
}

function fromRequest<T>(source: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    source.onsuccess = () => resolve(source.result);
    source.onerror = () => reject(source.error ?? new Error('IndexedDB 操作失败'));
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB 事务被中止'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB 事务失败'));
  });
}

export function createIdbPersistence(db: IDBDatabase): TilePersistence {
  async function loadMeta(sourceId?: string): Promise<readonly TileEntryMeta[]> {
    const tx = db.transaction(META_STORE, 'readonly');
    const store = tx.objectStore(META_STORE);
    const rows =
      sourceId === undefined
        ? await fromRequest<StoredMeta[]>(store.getAll())
        : await fromRequest<StoredMeta[]>(store.index(SOURCE_INDEX).getAll(sourceId));
    await transactionDone(tx);
    return rows.map(fromStoredMeta);
  }

  async function removeKeys(keys: readonly string[]): Promise<void> {
    if (keys.length === 0) return;
    const tx = db.transaction([TILES_STORE, META_STORE], 'readwrite');
    const tiles = tx.objectStore(TILES_STORE);
    const meta = tx.objectStore(META_STORE);
    for (const key of keys) {
      tiles.delete(key);
      meta.delete(key);
    }
    await transactionDone(tx);
  }

  return {
    async read(key): Promise<TileEntry | null> {
      const tx = db.transaction([TILES_STORE, META_STORE], 'readonly');
      // 两个请求都在 await 之前发出，因此共享同一个事务
      const metaRequest = fromRequest<StoredMeta | undefined>(tx.objectStore(META_STORE).get(key));
      const dataRequest = fromRequest<StoredTile | undefined>(tx.objectStore(TILES_STORE).get(key));
      const [metaRow, dataRow] = await Promise.all([metaRequest, dataRequest]);
      await transactionDone(tx);
      if (metaRow === undefined) return null;
      return { ...fromStoredMeta(metaRow), data: dataRow?.data ?? new ArrayBuffer(0) };
    },

    loadMeta,

    async write(entry): Promise<void> {
      const tx = db.transaction([TILES_STORE, META_STORE], 'readwrite');
      // "上游没有这块瓦片"只写 meta：占位记录不该占用字节表
      if (entry.state === 'ok') tx.objectStore(TILES_STORE).put(toStoredTile(entry));
      tx.objectStore(META_STORE).put(toStoredMeta(entry));
      await transactionDone(tx);
    },

    async touch(key, lastUsedAt): Promise<void> {
      const tx = db.transaction(META_STORE, 'readwrite');
      const store = tx.objectStore(META_STORE);
      const existing = await fromRequest<StoredMeta | undefined>(store.get(key));
      if (existing !== undefined) store.put({ ...existing, lastUsedAt });
      await transactionDone(tx);
    },

    delete: removeKeys,

    async clear(sourceId): Promise<void> {
      if (sourceId === undefined) {
        const tx = db.transaction([TILES_STORE, META_STORE], 'readwrite');
        tx.objectStore(TILES_STORE).clear();
        tx.objectStore(META_STORE).clear();
        await transactionDone(tx);
        return;
      }
      await removeKeys((await loadMeta(sourceId)).map((meta) => meta.key));
    },
  };
}

/*
 * 存进 IDB 的形状刻意与领域模型分开：字节在 `tiles` 表，其余在 `meta` 表。
 * 两张表的 schema 因此可以各自演进（给 meta 加"预缓存/手动缓存"之类的标记
 * 不需要动字节表）。
 */

interface StoredTile {
  readonly key: string;
  readonly data: ArrayBuffer;
}

interface StoredMeta {
  readonly key: string;
  readonly sourceId: string;
  readonly z: number;
  readonly x: number;
  readonly y: number;
  readonly bytes: number;
  readonly contentType: string;
  readonly state: 'ok' | 'empty';
  readonly storedAt: number;
  readonly lastUsedAt: number;
  readonly expiresAt: number | null;
}

function toStoredTile(entry: TileEntry): StoredTile {
  return { key: entry.key, data: entry.data };
}

function toStoredMeta(entry: TileEntryMeta): StoredMeta {
  return {
    key: entry.key,
    sourceId: entry.sourceId,
    z: entry.tile.z,
    x: entry.tile.x,
    y: entry.tile.y,
    bytes: entry.bytes,
    contentType: entry.contentType,
    state: entry.state,
    storedAt: entry.storedAt,
    lastUsedAt: entry.lastUsedAt,
    expiresAt: entry.expiresAt,
  };
}

function fromStoredMeta(stored: StoredMeta): TileEntryMeta {
  return {
    key: stored.key,
    sourceId: stored.sourceId,
    tile: { z: stored.z, x: stored.x, y: stored.y },
    bytes: stored.bytes,
    contentType: stored.contentType,
    state: stored.state,
    storedAt: stored.storedAt,
    lastUsedAt: stored.lastUsedAt,
    expiresAt: stored.expiresAt,
  };
}
